import { useState } from 'react';
import {
  IconSearch,
  IconUsers,
  IconMapPin,
  IconDeviceTvOld,
  IconPresentationAnalytics,
  IconVideo,
  IconCheckbox,
} from '@tabler/icons-react';
import { Badge, Box, Button, Card, Group, Select, SimpleGrid, Stack, Text, TextInput, Title } from '@/components/ui';
import { mockMeetingRooms, MeetingRoom } from '../mockData/calendar';

const getAmenityIcon = (amenity: string) => {
  const lower = amenity.toLowerCase();
  if (lower.includes('tv') || lower.includes('apple')) return IconDeviceTvOld;
  if (lower.includes('projector') || lower.includes('presentation')) return IconPresentationAnalytics;
  if (lower.includes('video')) return IconVideo;
  return IconCheckbox;
};

export default function RoomsPage() {
  const [search, setSearch] = useState('');
  const [locationFilter, setLocationFilter] = useState<string | null>(null);
  const [availableOnly, setAvailableOnly] = useState(false);

  const locations = [...new Set(mockMeetingRooms.map(r => r.location))];

  const filteredRooms = mockMeetingRooms.filter(room => {
    const matchesSearch = room.name.toLowerCase().includes(search.toLowerCase());
    const matchesLocation = !locationFilter || room.location === locationFilter;
    const matchesAvailable = !availableOnly || room.available;
    return matchesSearch && matchesLocation && matchesAvailable;
  });

  const availableCount = mockMeetingRooms.filter(r => r.available).length;

  return (
    <Stack gap="lg">
      {/* Header */}
      <Group justify="space-between">
        <Box>
          <Title order={2}>Meeting Rooms</Title>
          <Text c="dimmed" size="sm">{availableCount} of {mockMeetingRooms.length} rooms available</Text>
        </Box>
        <Button>Book Room</Button>
      </Group>

      {/* Filters */}
      <Card>
        <Group>
          <TextInput
            placeholder="Search rooms..."
            leftSection={<IconSearch size={16} />}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ flex: 1 }}
          />
          <Select
            placeholder="Location"
            data={locations.map(l => ({ value: l, label: l }))}
            value={locationFilter}
            onChange={setLocationFilter}
            clearable
            w={150}
          />
          <Button
            variant={availableOnly ? 'filled' : 'light'}
            onClick={() => setAvailableOnly(!availableOnly)}
          >
            Available Only
          </Button>
        </Group>
      </Card>

      {/* Rooms Grid */}
      <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }}>
        {filteredRooms.map(room => (
          <Card key={room.id}>
            <Stack gap="md">
              <Group justify="space-between">
                <Box>
                  <Text fw={600}>{room.name}</Text>
                  <Group gap={4}>
                    <IconMapPin size={14} color="var(--mantine-color-dimmed)" />
                    <Text size="sm" c="dimmed">{room.location}</Text>
                  </Group>
                </Box>
                <Badge size="sm" color={room.available ? 'green' : 'red'}>
                  {room.available ? 'Available' : 'Occupied'}
                </Badge>
              </Group>

              <Group gap="xs">
                <IconUsers size={16} color="var(--mantine-color-dimmed)" />
                <Text size="sm" c="dimmed">Capacity: {room.capacity} people</Text>
              </Group>

              <Box>
                <Text size="xs" c="dimmed" mb="xs">Amenities</Text>
                <Group gap="xs">
                  {room.amenities.map(amenity => {
                    const Icon = getAmenityIcon(amenity);
                    return (
                      <Badge
                        key={amenity}
                        size="xs"
                        variant="light"
                        leftSection={<Icon size={12} />}
                      >
                        {amenity}
                      </Badge>
                    );
                  })}
                </Group>
              </Box>

              <Button
                variant={room.available ? 'filled' : 'light'}
                disabled={!room.available}
                fullWidth
              >
                {room.available ? 'Book Now' : 'View Schedule'}
              </Button>
            </Stack>
          </Card>
        ))}
      </SimpleGrid>
    </Stack>
  );
}
